import { useMemo } from 'react'
import { ArrowDown } from 'lucide-react'
import { EditorialHeader } from '@/components/landing/Editorial'
import { HeroReveal, StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'
import { CardTornado } from '@/components/landing/listing'
import { DockMarquee } from '@/components/landing/service'
import type { IndustryData } from '@/data/industries'
import { agentTiles } from './AgentTiles'
import { documentTornadoCards } from './DocCards'
import { IndustryCtaBand } from './IndustryCtaBand'
import { IndustryListing } from './listing'
import { RotatedPainCards } from './RotatedPainCards'

/**
 * Card-tornado layout (Insurance; works reference): split hero with a small
 * bottom-aligned H1 and a tall helix of the industry's document cards, the
 * pain points as rotated cards, the agents as a dock-magnify marquee, the
 * filterable listing and a pink CTA band.
 */
export function TornadoLayout({ industry }: { industry: IndustryData }) {
  const cards = useMemo(() => documentTornadoCards(industry), [industry])
  const tiles = useMemo(() => agentTiles(industry.aiAgents), [industry])

  return (
    <>
      {/* 1. Hero: small H1 bottom-left, document tornado right */}
      <section className="relative -mt-[72px] overflow-hidden">
        <div className="landing-container grid lg:min-h-[100svh] lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-10">
          <div className="flex flex-col justify-end pb-6 pt-[calc(72px+3.5rem)] lg:pb-20">
            <HeroReveal delay={0.05}>
              <h1 className="landing-text-display-lg max-w-[18ch] text-[var(--color-landing-gray-900)] dark:text-white">
                <span className="landing-highlight landing-highlight--secondary">{industry.shortName}.</span>{' '}
                {industry.headline}
              </h1>
            </HeroReveal>
            <HeroReveal delay={0.15}>
              <p className="landing-text-body-lg mt-6 max-w-[42ch] text-pretty text-[var(--color-landing-gray-900)]/65 dark:text-white/65">
                {industry.subheadline}
              </p>
            </HeroReveal>
            <HeroReveal delay={0.25}>
              <a href="#templates" className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep mt-10">
                See the templates
                <ArrowDown aria-hidden className="h-5 w-5" />
              </a>
            </HeroReveal>
          </div>

          <CardTornado cards={cards} className="md:h-[42rem] lg:-mr-[clamp(1.5rem,2vw,2rem)] lg:h-[100svh]" />
        </div>
      </section>

      {/* 2. Pain points as rotated process cards */}
      <section className="landing-section overflow-x-clip">
        <div className="landing-container">
          <EditorialHeader
            heading={
              <>
                Where the hours <span className="landing-highlight">go today.</span>
              </>
            }
            lede={`Document intensity: ${industry.documentIntensity}. Here is where that volume slows ${industry.shortName.toLowerCase()} teams down, and what changes.`}
            cta={{ label: 'Book a demo', href: '/contact' }}
          />
          <RotatedPainCards industry={industry} className="mt-16 lg:mt-6" />
        </div>
      </section>

      {/* 3. Agents: dock-magnify marquee, with the roster in words */}
      <section aria-labelledby="agents-heading" className="landing-section overflow-hidden bg-[var(--color-landing-gray-50)] dark:bg-[#12121a]">
        <div className="landing-container">
          <TextReveal as="h2" className="landing-text-display-xl max-w-[22ch] text-[var(--color-landing-gray-900)] dark:text-white">
            <span id="agents-heading">
              {industry.aiAgents.length} agents on <span className="landing-highlight landing-highlight--secondary">every file.</span>
            </span>
          </TextReveal>
        </div>
        <DockMarquee className="mt-4 md:mt-8" tiles={tiles} duration={50} />
        <div className="landing-container">
          <StaggerContainer amount={0.1} className="grid gap-x-10 gap-y-8 border-t border-[var(--color-landing-gray-300)] pt-10 sm:grid-cols-2 lg:grid-cols-3 dark:border-[var(--color-landing-gray-700)]">
            {industry.aiAgents.map((agent) => (
              <StaggerItem key={agent.name}>
                <h3 className="font-[family-name:var(--font-landing-subdisplay)] text-lg font-bold uppercase tracking-[0.01em] text-[var(--color-landing-gray-900)] dark:text-white">
                  {agent.name}, <span className="text-[var(--color-landing-secondary)] dark:text-[#d27bff]">{agent.role}</span>
                </h3>
                <p className="landing-text-body-sm mt-2 text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">{agent.description}.</p>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* 4. Listing with filters */}
      <IndustryListing industry={industry} />

      {/* 5. CTA band: pink */}
      <IndustryCtaBand industry={industry} tone="pink" />
    </>
  )
}
